/**
 * /market/:slug/checkout — página dedicada de checkout (todos los nichos).
 */
import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { loadMarketCatalog, type MarketCatalogResult } from "@/lib/market-catalog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { MarketCheckoutPage } from "@/components/market/MarketCheckoutPage";
import { MarketCheckoutSkeleton } from "@/components/market/MarketSkeletons";

const fetchCatalog = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data: slug }) => {
    return loadMarketCatalog(slug, { request: getRequest() });
  });

const FONT_LINK = {
  rel: "stylesheet",
  href: "https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;600;700;800&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@600&display=swap",
};

export const Route = createFileRoute("/market_/$slug/checkout")({
  pendingComponent: MarketCheckoutSkeleton,
  pendingMs: 0,
  loader: async ({ params }) => fetchCatalog({ data: params.slug }),
  component: CheckoutRoute,
  head: ({ params, loaderData }) => {
    const manifestLink = { rel: "manifest", href: `/api/manifest/${params.slug}` };
    const result = loaderData as MarketCatalogResult | undefined;
    const name = result?.ok ? result.data.tenant.name : params.slug;
    return {
      meta: [
        { title: `Checkout — ${name}` },
        { name: "description", content: `Finalizá tu pedido en ${name}` },
        { name: "robots", content: "noindex, nofollow" },
      ],
      links: [manifestLink, FONT_LINK],
    };
  },
});

function CheckoutRoute() {
  const { slug } = Route.useParams();
  const result = Route.useLoaderData() as MarketCatalogResult;

  const offline = renderMarketPublicOffline(result);
  if (offline) return offline;
  if (!result.ok) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6 text-center">
        <p className="text-muted-foreground">Checkout no disponible.</p>
      </div>
    );
  }

  return <MarketCheckoutPage slug={slug} data={result.data} />;
}
